import { useState } from "react"
import { assignWithBustedCache } from "shared/lib/utils"
import classnames from "classnames"
import parse from "html-react-parser"
import { post } from "shared/lib/api"
import SuccessModal from "app/components/billing/success_modal"
import styles from "./exclusive_offer.module.scss"

import proBadgeWhite from "images/plan-badges/pro-badge-white.svg?raw"
import proPlusBadgeWhite from "images/plan-badges/pro_plus-badge-white.svg?raw"

interface UserSubscription {
  hasDiscount: boolean
  paymentInterval: "month" | "year"
  tier: string
}

interface Props {
  billingUrl: string
  claimOfferUrl: string
  exploreUrl: string
  handleCancellation: func
  tierName: string
  trackMutationError: func
  userSubscription: UserSubscription
}

const ExclusiveOffer = ({
  billingUrl,
  claimOfferUrl,
  exploreUrl,
  handleCancellation,
  tierName,
  trackMutationError,
  userSubscription,
}: Props) => {
  const [loading, updateLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const errorMessage = "There was a problem claiming your offer. Please contact support for more information."

  let warningCopy
  if (userSubscription.hasDiscount) {
    warningCopy = "This will replace any existing promotions on your account."
  }

  const intervalLabel = userSubscription.paymentInterval === "month" ? "month" : "year"

  const handleModalClose = () => {
    assignWithBustedCache(billingUrl)
  }

  const handleOffer = () => {
    window.App.Utils?.logCustomDatadogAction?.("click_exclusive_offer")
    updateLoading(true)
    post(claimOfferUrl)
      .then(data => {
        if (data.ok) {
          setShowConfirmModal(true)
        } else {
          trackMutationError("userSubscriptionPromoCodeUpdate", data.error, errorMessage, updateLoading)
        }
      })
      .catch(error => {
        trackMutationError("userSubscriptionPromoCodeUpdate", error, errorMessage, updateLoading)
      })
  }

  const handleCancel = () => {
    window.App.Utils?.logCustomDatadogAction?.("click_cancel_subscription")
    handleCancellation(updateLoading)
  }

  return (
    <div className={styles.exclusive_offer_container}>
      <h1 className="mb2--phone mb2--small-phone">Exclusive offer</h1>

      <p className={styles.description}>
        Get 20% off the next {userSubscription.paymentInterval === "month" ? "month" : "year"} of IFTTT {tierName}.
      </p>
      {warningCopy && <p className={classnames("mt0", styles["exclusive-offer-warning"])}>{warningCopy}</p>}

      <div className={styles["offer-card-wrapper"]}>
        <div className={styles["offer-card"]}>
          <div className={styles["offer-card-text"]}>20% OFF</div>
          <div className={styles["offer-card-badge"]}>
            {parse(tierName === "Pro+" ? proPlusBadgeWhite : proBadgeWhite)}
          </div>
        </div>
      </div>

      <div className="cancellation-actions cancellation-actions--offer">
        <button className="button-primary" onClick={handleOffer} disabled={loading}>
          Claim offer
        </button>
        <button
          type="button"
          className={`button-secondary-outlined ${loading ? "disabled" : ""}`}
          onClick={handleCancel}
          disabled={loading}
        >
          No thanks, just cancel
        </button>
      </div>

      <SuccessModal
        title={`You've got 20% off your next ${intervalLabel} of IFTTT ${tierName}`}
        successContent={warningCopy ? <p>{warningCopy}</p> : undefined}
        visible={showConfirmModal}
        onClose={handleModalClose}
        buttons={[
          {
            label: "Back to Billing",
            onClick: handleModalClose,
            variant: "secondary",
          },
        ]}
      />
    </div>
  )
}

export default ExclusiveOffer
